import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

export const SalaryFormModal = ({
  isOpen,
  onClose,
  selectedRecord,
  formData,
  setFormData,
  onSubmit,
  employees,
  monthOptions,
  onEmployeeSelect,
  totals,
  formatCurrency
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="salary-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {selectedRecord ? 'Edit Salary Record' : 'Create Salary Record'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={formData.employee_id}
                onValueChange={onEmployeeSelect}
                disabled={!!selectedRecord}
              >
                <SelectTrigger data-testid="select-employee">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Month *</Label>
              <Select
                value={formData.month}
                onValueChange={(val) => setFormData({ ...formData, month: val })}
                disabled={!!selectedRecord}
              >
                <SelectTrigger data-testid="select-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs defaultValue="earnings" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="earnings">Earnings</TabsTrigger>
              <TabsTrigger value="deductions">Deductions</TabsTrigger>
            </TabsList>
            <TabsContent value="earnings" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'basic_pay', label: 'Basic Pay' },
                  { key: 'hra', label: 'HRA' },
                  { key: 'da', label: 'DA' },
                  { key: 'special_allowance', label: 'Special Allowance' },
                  { key: 'conveyance', label: 'Conveyance' },
                  { key: 'medical_allowance', label: 'Medical Allowance' },
                  { key: 'other_allowances', label: 'Other Allowances' },
                  { key: 'overtime', label: 'Overtime' },
                  { key: 'bonus', label: 'Bonus' },
                  { key: 'arrears', label: 'Arrears' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <Label>{label}</Label>
                    <Input
                      type="number"
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: Number(e.target.value) })}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="deductions" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'pf_deduction', label: 'PF Deduction' },
                  { key: 'esi_deduction', label: 'ESI Deduction' },
                  { key: 'tds_deduction', label: 'TDS/Tax' },
                  { key: 'professional_tax', label: 'Professional Tax' },
                  { key: 'leave_deduction', label: 'Leave Deduction' },
                  { key: 'loan_deduction', label: 'Loan Deduction' },
                  { key: 'advance_deduction', label: 'Advance Deduction' },
                  { key: 'other_deductions', label: 'Other Deductions' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <Label>{label}</Label>
                    <Input
                      type="number"
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: Number(e.target.value) })}
                    />
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500 uppercase">Gross Salary</p>
                <p className="text-lg font-semibold text-slate-900">{formatCurrency(totals.gross)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Deductions</p>
                <p className="text-lg font-semibold text-red-600">{formatCurrency(totals.deductions)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Net Pay</p>
                <p className="text-lg font-semibold text-emerald-600">{formatCurrency(totals.net)}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Remarks</Label>
            <Input
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Optional remarks for this record"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-testid="save-salary-btn">
              {selectedRecord ? 'Update' : 'Create'} Record
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalaryFormModal;
